document.addEventListener('DOMContentLoaded', () => {
  const galleries = Array.from(document.querySelectorAll('[data-media-gallery]'));
  if (!galleries.length) return;

  const makeButton = (className, label, text) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.setAttribute('aria-label', label);
    button.textContent = text;
    return button;
  };

  galleries.forEach((gallery, galleryIndex) => {
    if (gallery.dataset.enhancedGallery === 'true') return;
    gallery.dataset.enhancedGallery = 'true';

    const stage = gallery.querySelector('.media-stage');
    const slides = Array.from(gallery.querySelectorAll('[data-media-slide]'));
    const thumbnails = Array.from(gallery.querySelectorAll('[data-media-target]'));
    if (!stage || !slides.length) return;

    let currentIndex = Math.max(0, slides.findIndex((slide) => !slide.hidden));
    let touchStartX = 0;
    let touchStartY = 0;
    let suppressZoomUntil = 0;

    stage.classList.add('media-stage-enhanced');
    stage.tabIndex = 0;
    stage.setAttribute('role', 'region');
    stage.setAttribute('aria-label', 'Product image gallery');

    const counter = document.createElement('div');
    counter.className = 'media-gallery-counter';
    counter.setAttribute('aria-live', 'polite');

    const previousButton = makeButton('media-gallery-arrow media-gallery-prev', 'Previous product image', '‹');
    const nextButton = makeButton('media-gallery-arrow media-gallery-next', 'Next product image', '›');

    if (slides.length > 1) {
      stage.append(previousButton, nextButton, counter);
    }

    const hint = document.createElement('p');
    hint.className = 'media-gallery-hint';
    hint.textContent = slides.length > 1
      ? 'Tap an image to enlarge. Swipe or use the arrows to view more.'
      : 'Tap the image to enlarge.';
    gallery.insertBefore(hint, gallery.querySelector('.media-thumbnails'));

    const updateCounter = () => {
      counter.textContent = `${currentIndex + 1} / ${slides.length}`;
    };

    const showIndex = (index, { focusThumbnail = false } = {}) => {
      currentIndex = (index + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === currentIndex;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        if (!active) {
          slide.querySelectorAll('video').forEach((video) => video.pause());
        }
      });

      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const active = thumbnailIndex === currentIndex;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', String(active));
        if (active) {
          thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          if (focusThumbnail) thumbnail.focus({ preventScroll: true });
        }
      });

      updateCounter();
    };

    previousButton.addEventListener('click', () => showIndex(currentIndex - 1));
    nextButton.addEventListener('click', () => showIndex(currentIndex + 1));

    thumbnails.forEach((thumbnail, thumbnailIndex) => {
      thumbnail.addEventListener('click', () => showIndex(thumbnailIndex));
    });

    stage.addEventListener('keydown', (event) => {
      if (event.target !== stage) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showIndex(currentIndex - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        showIndex(currentIndex + 1);
      }
    });

    stage.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    }, { passive: true });

    stage.addEventListener('touchend', (event) => {
      if (slides.length < 2 || !event.changedTouches.length) return;
      const deltaX = event.changedTouches[0].clientX - touchStartX;
      const deltaY = event.changedTouches[0].clientY - touchStartY;
      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;

      suppressZoomUntil = Date.now() + 400;
      showIndex(deltaX < 0 ? currentIndex + 1 : currentIndex - 1);
    }, { passive: true });

    const imageEntries = slides.map((slide, slideIndex) => {
      const image = slide.dataset.mediaType === 'image' ? slide.querySelector('img') : null;
      return image ? { slideIndex, image } : null;
    }).filter(Boolean);

    if (!imageEntries.length) {
      showIndex(currentIndex);
      return;
    }

    const lightbox = document.createElement('div');
    lightbox.className = 'media-lightbox';
    lightbox.hidden = true;
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', 'Expanded product image');
    lightbox.setAttribute('aria-hidden', 'true');
    lightbox.innerHTML = `
      <button type="button" class="media-lightbox-close" aria-label="Close expanded image">×</button>
      <button type="button" class="media-lightbox-arrow media-lightbox-prev" aria-label="Previous expanded image">‹</button>
      <figure class="media-lightbox-figure">
        <img class="media-lightbox-image" alt="">
        <figcaption class="media-lightbox-counter" aria-live="polite"></figcaption>
      </figure>
      <button type="button" class="media-lightbox-arrow media-lightbox-next" aria-label="Next expanded image">›</button>
    `;
    document.body.appendChild(lightbox);

    const closeButton = lightbox.querySelector('.media-lightbox-close');
    const lightboxPrevious = lightbox.querySelector('.media-lightbox-prev');
    const lightboxNext = lightbox.querySelector('.media-lightbox-next');
    const lightboxImage = lightbox.querySelector('.media-lightbox-image');
    const lightboxCounter = lightbox.querySelector('.media-lightbox-counter');
    const focusableControls = [closeButton, lightboxPrevious, lightboxNext];
    let lightboxImageIndex = 0;
    let returnFocus = null;

    const largeImageUrl = (image) => {
      const source = image.currentSrc || image.src;
      if (!source) return '';
      if (/([?&])width=\d+/.test(source)) {
        return source.replace(/([?&])width=\d+/, '$1width=2400');
      }
      return source;
    };

    const updateLightbox = (imageIndex) => {
      lightboxImageIndex = (imageIndex + imageEntries.length) % imageEntries.length;
      const entry = imageEntries[lightboxImageIndex];
      lightboxImage.src = largeImageUrl(entry.image);
      lightboxImage.alt = entry.image.alt || 'Expanded product image';
      lightboxCounter.textContent = `${lightboxImageIndex + 1} / ${imageEntries.length}`;
      lightboxPrevious.hidden = imageEntries.length < 2;
      lightboxNext.hidden = imageEntries.length < 2;
      showIndex(entry.slideIndex);
    };

    const openLightbox = (entryIndex, trigger) => {
      returnFocus = trigger;
      updateLightbox(entryIndex);
      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('media-lightbox-open');
      closeButton.focus();
    };

    const closeLightbox = () => {
      lightbox.hidden = true;
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('media-lightbox-open');
      lightboxImage.removeAttribute('src');
      if (returnFocus && document.contains(returnFocus)) returnFocus.focus({ preventScroll: true });
    };

    imageEntries.forEach((entry, entryIndex) => {
      const image = entry.image;
      image.classList.add('media-zoomable');
      image.setAttribute('role', 'button');
      image.setAttribute('tabindex', '0');
      image.setAttribute('aria-label', `Enlarge product image ${entryIndex + 1}`);
      image.draggable = false;

      image.addEventListener('click', () => {
        if (Date.now() < suppressZoomUntil) return;
        openLightbox(entryIndex, image);
      });

      image.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openLightbox(entryIndex, image);
      });
    });

    closeButton.addEventListener('click', closeLightbox);
    lightboxPrevious.addEventListener('click', () => updateLightbox(lightboxImageIndex - 1));
    lightboxNext.addEventListener('click', () => updateLightbox(lightboxImageIndex + 1));

    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox) closeLightbox();
    });

    lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeLightbox();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        updateLightbox(lightboxImageIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        updateLightbox(lightboxImageIndex + 1);
      } else if (event.key === 'Tab') {
        const visibleControls = focusableControls.filter((control) => !control.hidden);
        const currentControlIndex = visibleControls.indexOf(document.activeElement);
        if (event.shiftKey && currentControlIndex <= 0) {
          event.preventDefault();
          visibleControls[visibleControls.length - 1].focus();
        } else if (!event.shiftKey && currentControlIndex === visibleControls.length - 1) {
          event.preventDefault();
          visibleControls[0].focus();
        }
      }
    });

    showIndex(currentIndex);
  });
});
